/**
 * usePlaceEntryScreen.js
 * ----------------------
 * Hook for Place create/edit form screen using shared useMasterEntry.
 * Loads active places for parent territory assignment.
 * 100% conforming to module_implementation_standards.md.
 */
import { useState, useEffect } from 'react';
import { useMasterEntry } from '../../common/useMasterEntry';
import * as placeApi from '../api/placeApi';

export function usePlaceEntryScreen() {
  const [parentOptions, setParentOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const master = useMasterEntry({
    entityName: 'Place',
    apiClient: placeApi.placeClient,
    fetchDetail: placeApi.getPlaceDetail,
    createItem: placeApi.createPlace,
    updateItem: placeApi.updatePlace,
    initialForm: {
      name: '',
      parent_id: '',
      remarks: '',
    },
    validate: (form) => {
      if (!form.name || !form.name.trim()) {
        return 'Place / Locality Name is required.';
      }
      return null;
    },
    listPath: '/masters/place/list',
    transformPayload: (form, operationalStatus) => ({
      name: form.name.trim(),
      parent_id: form.parent_id ? Number(form.parent_id) : 0,
      remarks: form.remarks ? form.remarks.trim() : null,
      status: operationalStatus === 'active' ? 'Active' : 'Archived',
    }),
    transformDetail: (detail) => ({
      form: {
        name: detail.name || '',
        parent_id: detail.parent_id ? String(detail.parent_id) : '',
        remarks: detail.remarks || '',
      },
      operationalStatus: detail.status === 'Active' ? 'active' : 'inactive',
    }),
  });

  useEffect(() => {
    let isMounted = true;
    setLoadingOptions(true);
    placeApi.getPlaceOptions()
      .then((options) => {
        if (!isMounted) return;
        const normalized = Array.isArray(options) ? options : [];
        setParentOptions(normalized);
      })
      .catch((err) => {
        console.error('Failed to load parent place options:', err);
      })
      .finally(() => {
        if (isMounted) setLoadingOptions(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Filter out self from parent options when editing
  const filteredParentOptions = master.state.id
    ? parentOptions.filter((opt) => String(opt.id) !== String(master.state.id))
    : parentOptions;

  return {
    ...master,
    state: {
      ...master.state,
      parentOptions: filteredParentOptions,
      loadingOptions,
    },
  };
}
